import { ArrowLeft } from 'lucide-react';
import { cn } from '@/components/ui/utils';

// ─── Tool registry ───────────────────────────────────────────────

type ToolStatus = 'active' | 'coming-soon';
type ToolCategory = 'omniad' | 'web' | 'analytics' | 'ads' | 'crm' | 'email' | 'social' | 'agent' | 'content';

interface Tool {
  id: string;
  name: string;
  description: string;
  category: ToolCategory;
  status: ToolStatus;
  emoji: string;
  agentNames?: string[];   // which agents actively use this
}

const TOOLS: Tool[] = [
  // ── Active — Omni Ad Native ────────────────────────────────────
  {
    id: 'brand-memory',
    name: 'Brand Memory',
    description: 'Injects your Brand OS — identity, voice, audience, and competitors — into every agent prompt automatically.',
    category: 'omniad',
    status: 'active',
    emoji: '🧠',
    agentNames: ['All agents'],
  },
  {
    id: 'agent-memory',
    name: 'Agent Memory',
    description: 'Agents save insights, decisions, and preferences to persistent memory that carries across conversations.',
    category: 'omniad',
    status: 'active',
    emoji: '💾',
    agentNames: ['All agents'],
  },
  {
    id: 'creator-discovery',
    name: 'Creator Discovery',
    description: 'Searches the Omni Ad creator database to find influencers by niche, follower range, engagement rate, and platform.',
    category: 'omniad',
    status: 'active',
    emoji: '🎤',
    agentNames: ['Rex'],
  },
  {
    id: 'performance-data',
    name: 'Performance Data',
    description: 'Pulls paid media performance metrics (Meta, TikTok) from your data lake — spend, ROAS, CPM, CTR by campaign.',
    category: 'omniad',
    status: 'active',
    emoji: '📊',
    agentNames: ['Lena', 'Kai'],
  },

  // ── Active — Web & Research ────────────────────────────────────
  {
    id: 'web-search',
    name: 'Web Search',
    description: 'Searches the web in real time via Google Custom Search or DuckDuckGo — for competitor research, trends, and news.',
    category: 'web',
    status: 'active',
    emoji: '🔍',
    agentNames: ['All agents'],
  },
  {
    id: 'web-fetch',
    name: 'Web Fetch',
    description: 'Reads any URL and extracts clean content using Mozilla Readability — articles, pages, docs, and more.',
    category: 'web',
    status: 'active',
    emoji: '🌐',
    agentNames: ['All agents'],
  },

  // ── Active — Content Generation ─────────────────────────────────
  {
    id: 'propose-content',
    name: 'Content Proposals',
    description: 'Creates draft social media posts for any platform — LinkedIn, Instagram, TikTok, Facebook — with copy and hashtags.',
    category: 'content',
    status: 'active',
    emoji: '✍️',
    agentNames: ['Aria', 'Nova', 'Joanya', 'Lena'],
  },
  {
    id: 'generate-image',
    name: 'AI Image Generation',
    description: 'Generates custom visuals using Google Gemini — with brand logo compositing and style-matched creative.',
    category: 'content',
    status: 'active',
    emoji: '🎨',
    agentNames: ['Jones'],
  },
  {
    id: 'generate-pptx',
    name: 'PowerPoint Generator',
    description: 'Creates professional slide decks with 8 slide types — title, metrics, charts, columns, quotes, and closings.',
    category: 'content',
    status: 'active',
    emoji: '📑',
    agentNames: ['Jones', 'All agents'],
  },
  {
    id: 'generate-xlsx',
    name: 'Excel Generator',
    description: 'Creates styled spreadsheets with headers, auto-filters, column widths, and data formatting.',
    category: 'content',
    status: 'active',
    emoji: '📗',
    agentNames: ['Kai', 'Lena', 'All agents'],
  },
  {
    id: 'generate-docx',
    name: 'Word Document Generator',
    description: 'Creates formatted Word documents with headings, tables, bullet lists, and professional styling.',
    category: 'content',
    status: 'active',
    emoji: '📄',
    agentNames: ['All agents'],
  },

  // ── Active — Agent Capabilities ─────────────────────────────────
  {
    id: 'create-task',
    name: 'Task Delegation',
    description: 'Agents create and assign tasks to other team members — with priority, deadlines, and goal context.',
    category: 'agent',
    status: 'active',
    emoji: '📋',
    agentNames: ['All agents'],
  },
  {
    id: 'task-management',
    name: 'Task Management',
    description: 'Agents can list, update status, and delete tasks on the board — clean up duplicates, change priorities, etc.',
    category: 'agent',
    status: 'active',
    emoji: '✅',
    agentNames: ['All agents'],
  },
  {
    id: 'read-file',
    name: 'File Reading',
    description: 'Reads uploaded files from the workspace — PDFs, CSVs, text files — for data analysis and reference.',
    category: 'agent',
    status: 'active',
    emoji: '📁',
    agentNames: ['Kai', 'Lena', 'Joanya'],
  },
  {
    id: 'run-script',
    name: 'Script Execution',
    description: 'Runs shell commands for data processing — sandboxed with 30-second timeout and output limits.',
    category: 'agent',
    status: 'active',
    emoji: '⚡',
    agentNames: ['Kai', 'Lena', 'Joanya'],
  },
  {
    id: 'suggested-replies',
    name: 'Smart Replies',
    description: 'Suggests contextual reply buttons after responses — helping users continue the conversation naturally.',
    category: 'agent',
    status: 'active',
    emoji: '💬',
    agentNames: ['All agents'],
  },

  // ── Active — SEO & Webflow ──────────────────────────────────────
  {
    id: 'seo-search',
    name: 'SEO Research Suite',
    description: 'Google organic search, autocomplete, news, People Also Ask, related searches, and rank checking.',
    category: 'analytics',
    status: 'active',
    emoji: '🔎',
    agentNames: ['Joanya'],
  },
  {
    id: 'seo-page-audit',
    name: 'On-Page SEO Audit',
    description: 'Analyzes any page for SEO issues — meta tags, headings, images, schema markup, and content quality.',
    category: 'analytics',
    status: 'active',
    emoji: '🩺',
    agentNames: ['Joanya'],
  },
  {
    id: 'webflow-cms',
    name: 'Webflow CMS',
    description: 'Read and write Webflow pages, CMS items, manage 301 redirects, and update robots.txt — direct site management.',
    category: 'web',
    status: 'active',
    emoji: '🔧',
    agentNames: ['Joanya'],
  },

  // ── Coming soon ─────────────────────────────────────────────────
  {
    id: 'google-analytics',
    name: 'Google Analytics',
    description: 'Traffic, conversions, and user behaviour data from GA4 — so agents can ground recommendations in real site data.',
    category: 'analytics',
    status: 'coming-soon',
    emoji: '📈',
    agentNames: ['Joanya', 'Kai'],
  },
  {
    id: 'meta-ads',
    name: 'Meta Ads Manager',
    description: 'Live ad account data — campaigns, ad sets, creatives, audiences, and spend — directly from the Meta Ads API.',
    category: 'ads',
    status: 'coming-soon',
    emoji: '📘',
    agentNames: ['Lena', 'Kai'],
  },
  {
    id: 'google-ads',
    name: 'Google Ads',
    description: 'Search and shopping campaign performance, keyword bid data, quality scores, and impression share.',
    category: 'ads',
    status: 'coming-soon',
    emoji: '🔵',
    agentNames: ['Lena', 'Kai'],
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'CRM contacts, deals, pipeline stages, and engagement history — for agents that work with sales and lead gen.',
    category: 'crm',
    status: 'coming-soon',
    emoji: '🟠',
    agentNames: ['Nova'],
  },
  {
    id: 'gmail',
    name: 'Gmail',
    description: 'Read, draft, and send emails — enables agents to manage outreach, reply to leads, and track email threads.',
    category: 'email',
    status: 'coming-soon',
    emoji: '📧',
    agentNames: ['Rex', 'Nova'],
  },
  {
    id: 'instagram-graph',
    name: 'Instagram Graph API',
    description: 'Follower growth, reach, impressions, and top content performance from your Instagram business account.',
    category: 'social',
    status: 'coming-soon',
    emoji: '📷',
    agentNames: ['Aria', 'Rex'],
  },
  {
    id: 'tiktok-business',
    name: 'TikTok for Business',
    description: 'Video analytics, audience insights, and ad performance from the TikTok Business API.',
    category: 'social',
    status: 'coming-soon',
    emoji: '🎵',
    agentNames: ['Aria', 'Rex'],
  },
  {
    id: 'youtube-analytics',
    name: 'YouTube Analytics',
    description: 'Views, watch time, subscriber trends, and top video performance from your YouTube channel.',
    category: 'social',
    status: 'coming-soon',
    emoji: '▶️',
    agentNames: ['Aria', 'Lena'],
  },
  {
    id: 'semrush',
    name: 'SEMrush',
    description: 'Domain analytics, keyword rankings, backlink data, and competitor traffic estimates.',
    category: 'analytics',
    status: 'coming-soon',
    emoji: '🔬',
    agentNames: ['Joanya', 'Nova'],
  },
  {
    id: 'ahrefs',
    name: 'Ahrefs',
    description: 'Backlink profiles, keyword opportunities, and content gap analysis via the Ahrefs API.',
    category: 'analytics',
    status: 'coming-soon',
    emoji: '🔗',
    agentNames: ['Joanya'],
  },
];

const CATEGORY_LABELS: Record<ToolCategory, string> = {
  omniad: 'Omni Ad Native',
  web: 'Web & Data',
  analytics: 'Analytics & SEO',
  content: 'Content Generation',
  agent: 'Agent Capabilities',
  ads: 'Paid Ads',
  crm: 'CRM & Sales',
  email: 'Email',
  social: 'Social Media',
};

const CATEGORY_COLORS: Record<ToolCategory, string> = {
  omniad: '#be43ff',
  web: '#3b82f6',
  analytics: '#10b981',
  content: '#f59e0b',
  agent: '#6366f1',
  ads: '#f97316',
  crm: '#ef4444',
  email: '#ec4899',
  social: '#8b5cf6',
};

// ─── Component ────────────────────────────────────────────────────

export function ToolsPage({ onBack }: { onBack: () => void }) {
  const active = TOOLS.filter(t => t.status === 'active');
  const coming = TOOLS.filter(t => t.status === 'coming-soon');

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-[#fafafa]">
      <div className="flex-1 overflow-y-auto px-8 py-7">
        <div className="max-w-[860px] mx-auto">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-[12px] text-[#667085] hover:text-[#344054] transition-colors mb-5"
            style={{ fontWeight: 500 }}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
          </button>

          <div className="mb-7">
            <h1 className="text-[22px] text-[#101828]" style={{ fontWeight: 700 }}>
              Tools & Connectors
            </h1>
            <p className="text-[13px] text-[#667085] mt-0.5">
              Data sources and integrations available to your agents. Active tools are injected automatically during skill execution.
            </p>
          </div>

          {/* Active tools */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[11px] text-[#101828] uppercase tracking-wider" style={{ fontWeight: 700 }}>
                Active
              </span>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#ecfdf5] text-[#059669]" style={{ fontWeight: 600 }}>
                {active.length} tools
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {active.map(tool => (
                <ToolCard key={tool.id} tool={tool} />
              ))}
            </div>
          </div>

          {/* Coming soon */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[11px] text-[#101828] uppercase tracking-wider" style={{ fontWeight: 700 }}>
                Coming soon
              </span>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#f3f4f6] text-[#667085]" style={{ fontWeight: 600 }}>
                {coming.length} connectors
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {coming.map(tool => (
                <ToolCard key={tool.id} tool={tool} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToolCard({ tool }: { tool: Tool }) {
  const isActive = tool.status === 'active';
  const categoryColor = CATEGORY_COLORS[tool.category];

  return (
    <div
      className={cn(
        'bg-white border rounded-2xl p-4 transition-all',
        isActive ? 'border-gray-100 shadow-sm' : 'border-gray-100 opacity-60'
      )}
    >
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <div className="flex items-center gap-2.5">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center text-lg shrink-0"
            style={{ background: `${categoryColor}12` }}
          >
            {tool.emoji}
          </div>
          <div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[13px] text-[#101828]" style={{ fontWeight: 700 }}>
                {tool.name}
              </span>
            </div>
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-md"
              style={{
                background: `${categoryColor}15`,
                color: categoryColor,
                fontWeight: 600,
              }}
            >
              {CATEGORY_LABELS[tool.category]}
            </span>
          </div>
        </div>
        {isActive ? (
          <span
            className="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-[#ecfdf5] text-[#059669] flex items-center gap-1"
            style={{ fontWeight: 600 }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#10b981] inline-block" />
            Active
          </span>
        ) : (
          <span
            className="shrink-0 text-[10px] px-2 py-0.5 rounded-full bg-[#f3f4f6] text-[#98a2b3]"
            style={{ fontWeight: 600 }}
          >
            Soon
          </span>
        )}
      </div>

      <p className="text-[12px] text-[#667085] mb-3" style={{ lineHeight: '1.5' }}>
        {tool.description}
      </p>

      {tool.agentNames && tool.agentNames.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] text-[#98a2b3]" style={{ fontWeight: 500 }}>
            Used by:
          </span>
          {tool.agentNames.map(name => (
            <span
              key={name}
              className="text-[10px] px-2 py-0.5 rounded-full bg-[#f9fafb] text-[#667085] border border-gray-100"
              style={{ fontWeight: 500 }}
            >
              {name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
